import React, { useState } from 'react'
import Cookies from 'js-cookie'

import SchoolSelectionList from './v3/SchoolSelectionList'
import CopyResultsMessage from './v3/CopyResultsMessage'
import BubbleMessageStack from './v3/BubbleMessageStack'

const copySharedFormToSchools = (shareToken, selectedSchoolIds, updates, callback) => {
  const csrftoken = Cookies.get('csrftoken')
  fetch(`/api/v1/forms/copy-shared-form/${shareToken}`,
    {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'X-CSRFToken': csrftoken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ schools: selectedSchoolIds, updates }),
    })
    .then(response => response.json())
    .then(obj => {
      if (obj.error) {
        alert(I18N._("Failed to copy questionnaire") + ": " + JSON.stringify(obj))
      } else {
        callback(obj)
      }
    })
    .catch(err => {
      console.error(err)
      alert(I18N._("Error requesting data from backend") + ": " + err)
    })
}

const ReviewQuestionArea = ({ area, questions }) => {
  const [expanded, setExpanded] = useState(false)

  const toggleExpanded = (ev) => {
    ev.preventDefault()
    setExpanded(!expanded)
    return false
  }

  const areaName = area ? area.category : I18N._("Unknown area")

  return (
    <div className={"col-xs-12 form-group" + (area && area.mandatory ? " mandatory" : "")}>
      {areaName}
      <a href="#" onClick={toggleExpanded}>
        {questions.length} {I18N._("questions")}
        <span
          className={"glyphicon glyphicon-chevron-" + (expanded ? "up" : "down")}
          aria-hidden="true">
        </span>
      </a>
      {expanded &&
        <ul className="questions-list">
          {questions.map((q, i) => <li key={i}>{q.text}</li>)}
        </ul>
      }
    </div>
  )
}

export default ({ share_token, name = "", description = "", greeting_text = "", target_audience_display = "", question_areas = [], questions = {} }) => {
  const validAreaIds = new Set(question_areas.map((area) => area.id))
  const activeQuestions = Object.values(questions).filter((q) => q.active)
  const [copyResults, setCopyResults] = useState(null)
  const [copyName, setCopyName] = useState(name)
  const [copyDescription, setCopyDescription] = useState(description)
  const [copyGreetingText, setCopyGreetingText] = useState(greeting_text || "")

  const addQuestionnaireToSchools = (selectedSchoolIds) => {
    const updates = { name: copyName, description: copyDescription, greeting_text: copyGreetingText }
    copySharedFormToSchools(share_token, selectedSchoolIds, updates, (obj) => {
      setCopyResults({ success: obj.success, fail: obj.fail })
    })
  }

  const groupedByArea = activeQuestions.reduce((acc, q) => {
    if (q.area_id !== null && validAreaIds.has(q.area_id)) {
      acc[q.area_id] = acc[q.area_id] || []
      acc[q.area_id].push(q)
    }
    return acc
  }, {})

  const extraQuestions = activeQuestions.filter((q) => q.area_id === null || !validAreaIds.has(q.area_id))
  const totalQuestions = activeQuestions.length

  return (
    <React.Fragment>
      <h2>
        {I18N._("Review shared questionnaire")}
      </h2>
      <p className="vertical-spacing-bottom">
        {I18N._("Review the shared questionnaire and optionally change the description, name and greeting text. Select from your adminstered schools where you would like the questionnaire to be added. To prevent duplicates the system will check that a questionnaire with the exact same questions does not already exist in the target schools.")}
      </p>

      <BubbleMessageStack>
        <CopyResultsMessage results={copyResults} />
      </BubbleMessageStack>

      <div className="form-group row odd vertical-padding target-audience">
        <div className="col-sm-4 col-xs-12">
          <label className="control-label">
            {I18N._("Questionnaire audience")}
          </label>
        </div>
        <div className="col-sm-8 col-xs-12">
          <b>{target_audience_display}</b>
        </div>
      </div>

      <div className="form-group row even vertical-padding question-areas-container">
        <div className="col-sm-4 col-xs-12">
          <label className="control-label">
            {I18N._("Included areas")}
          </label>
          <p className="help-block">
            <span dangerouslySetInnerHTML={{ __html: I18N._("There are <span>%(questions_in_total)s</span> questions in total", { params: { questions_in_total: totalQuestions } }) }}>
            </span>.
          </p>
        </div>
        <div className="col-sm-8 col-xs-12 question-areas">
          <div className="questions-list">
            {
              Object.keys(groupedByArea).map((areaId) =>
                <ReviewQuestionArea
                  key={areaId}
                  area={question_areas.find((a) => a.id === parseInt(areaId, 10))}
                  questions={groupedByArea[areaId]} />
              )
            }
          </div>
        </div>
      </div>

      <div className="form-group row odd vertical-padding">
        <div className="col-sm-4 col-xs-12">
          <label className="control-label">
            {I18N._("Extra questions")}
          </label>
        </div>
        <div className="col-sm-8 col-xs-12">
          {
            extraQuestions.length > 0 ?
              <ul className="questions-list">
                {extraQuestions.map((q) => <li key={q.id}>{q.text}</li>)}
              </ul>
              : <p>{I18N._("No extra questions")}</p>
          }
        </div>
      </div>

      <div className="form-group row even vertical-padding">
        <div className="col-sm-4 col-xs-12">
          <label htmlFor="copy-name" className="control-label">
            {I18N._("Questionnaire name")}
          </label>
        </div>
        <div className="col-sm-8 col-xs-12">
          <input type="text" id="copy-name" className="form-control"
            value={copyName}
            onChange={(ev) => setCopyName(ev.target.value)}
            placeholder={I18N._("Optional name for the questionnaire")} />
        </div>
      </div>

      <div className="form-group row odd vertical-padding">
        <div className="col-sm-4 col-xs-12">
          <label htmlFor="copy-description" className="control-label">
            {I18N._("Questionnaire description")}
          </label>
        </div>
        <div className="col-sm-8 col-xs-12">
          <textarea id="copy-description" className="form-control"
            value={copyDescription}
            onChange={(ev) => setCopyDescription(ev.target.value)}></textarea>
        </div>
      </div>

      <div className="form-group row even vertical-padding">
        <div className="col-sm-4 col-xs-12">
          <label htmlFor="copy-greeting-text" className="control-label">
            {I18N._("Questionnaire greeting")}
          </label>
        </div>
        <div className="col-sm-8 col-xs-12">
          <textarea id="copy-greeting-text" className="form-control"
            value={copyGreetingText}
            onChange={(ev) => setCopyGreetingText(ev.target.value)}></textarea>
        </div>
      </div>

      <div className="add-to-school form-group row odd vertical-padding">
        <div className="col-sm-4 col-xs-12">
          <label className="control-label">
            {I18N._("Add questionnaire to your school")}
          </label>
          <p className="help-block">
            {I18N._("Add this shared questionnaire to one of the schools you control. The schools will receive the same questions and the results can be compared.")}
          </p>
        </div>
        <div className="col-sm-8 col-xs-12">
          <SchoolSelectionList
            submitLabel={I18N._("Add questionnaire to schools")}
            onSubmit={addQuestionnaireToSchools}
          />
        </div>
      </div>

      <hr />
    </React.Fragment>
  )
}
